/**
 * Adapts a simulation session's turns into the NodeType shape that try-it
 * components already know how to render. Keeps simulation replay visually
 * identical to a live try-it run without forking the message components.
 */
import type { Context } from '@/shared/types/chat';
import type {
  BrainMessage,
  Correction,
  Entity,
  NodeType,
  ResponseMessage,
  ToolCallsPayload,
  UserMessage,
} from '@/shared/types/tryIt';

import type { SimulationTurn, SimulationTurnCorrection } from './types';

interface BuildResult {
  sessionNodes: NodeType[];
  context: Context[];
}

const toNodeCorrections = (
  list: SimulationTurnCorrection[] | null | undefined
): Correction[] | undefined => {
  if (!list?.length) {
    return undefined;
  }
  // ISO-8601 strings sort lexicographically, so string compare == chronological compare.
  const latest = list.reduce((a, b) => (a.time > b.time ? a : b));
  return [
    {
      task: 'synthesis',
      time: latest.time,
      language: latest.language,
      edited_by: latest.edited_by,
      correction: latest.correction,
    },
  ];
};

export const resolveTurnRequestId = (turn: SimulationTurn): string | undefined => {
  const stack = turn.output.debug?.nodes_stack ?? [];
  return turn.request_id ?? stack[stack.length - 1]?.request_id ?? stack[0]?.request_id;
};

export const buildTryItSessionFromTurns = (turns: SimulationTurn[] | undefined): BuildResult => {
  if (!turns?.length) {
    return { sessionNodes: [], context: [{}] };
  }

  const sessionNodes: NodeType[] = turns.map((turn) => {
    const nodesStack = turn.output.debug?.nodes_stack ?? [];
    const currentNode = nodesStack[nodesStack.length - 1];
    // Leave undefined when absent; downstream guards (e.g. amend gating) depend on it.
    const requestId = resolveTurnRequestId(turn);

    const userMessage: UserMessage = {
      type: 'user',
      text: turn.input.text,
      intents: (turn.output.intents ?? null) as UserMessage['intents'],
      entities: turn.output.entities as Entity[] | undefined,
      request_id: requestId,
    };

    const brainMessage: BrainMessage = {
      type: 'brain',
      responses: (turn.output.responses ?? []) as ResponseMessage[],
    };

    const toolsInvoked = turn.output.debug?.tools_invoked;
    const toolCalls: ToolCallsPayload | undefined = toolsInvoked?.length
      ? { tool_invocations: toolsInvoked }
      : undefined;

    return {
      id: currentNode?.node_id ?? null,
      name: currentNode?.name ?? null,
      author_type: 'brain',
      time: turn.sent_at,
      request_id: requestId,
      context: (turn.context ?? {}) as Context,
      debugLogs: turn.output.debug?.logs,
      steps: turn.output.debug?.steps as NodeType['steps'],
      nodes_stack: nodesStack as NodeType['nodes_stack'],
      standalone_question: turn.output.debug?.standalone_question,
      intents: turn.output.intents as NodeType['intents'],
      planner_intents: turn.output.planner_intents,
      messages: [userMessage, brainMessage],
      tool_calls: toolCalls,
      corrections: toNodeCorrections(turn.corrections),
    };
  });

  const context: Context[] = [{}, ...turns.map((turn) => (turn.context ?? {}) as Context)];

  return { sessionNodes, context };
};
